import React from "react";
import { Link } from "react-router-dom";
import { ChevronUp, Info } from "lucide-react";
import { useToast } from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import { ReactComponent as DishMark } from "../../../assets/svg/sidebar/dishes.svg";
import { ROUTES } from "../../../routes/paths";
import { useDishScope } from "../../mealPlans/hooks/useDishScope";
import type { DishScope } from "../../mealPlans/types/plans.types";

/**
 * One of the two ways the Dish search can be set, with a picture of what the
 * Dish field's list looks like under it. The picture is fixed: three rows
 * that show the difference — one of yours among the catalog's, or only yours
 * — rather than a live search, which would show whatever the workspace
 * happens to hold and might show nothing.
 */
interface ScopeChoice {
  scope: DishScope;
  title: string;
  description: string;
  /** Each row of the picture, and whether it wears the "yours" mark. */
  rows: { name: string; own: boolean }[];
}

const CHOICES: ScopeChoice[] = [
  {
    scope: "all",
    title: "All dishes",
    description: "Your dishes + Naurist database. Best when you want more choices.",
    rows: [
      { name: "Dosa", own: false },
      { name: "Ragi dosa", own: true },
      { name: "Masala dosa", own: false },
    ],
  },
  {
    scope: "mine",
    title: "My dishes only",
    description: "Only dishes you added. Faster when you plan with your own recipes.",
    rows: [
      { name: "Ragi dosa", own: true },
      { name: "Wheat dosa", own: true },
      { name: "Moong dal dosa", own: true },
    ],
  },
];

/**
 * What the Dish field's list looks like under a setting — its box with the
 * list open beneath, drawn the way `CustomDropdown` draws the real one, down
 * to the key to the mark pinned at the top.
 *
 * A picture of a control rather than a control: nothing in it can be pressed,
 * typed into or picked, and it says nothing to a screen reader. What it is
 * for is showing the difference between the two settings before one is
 * chosen, which the words above it can only describe.
 */
const ListPreview: React.FC<{ rows: ScopeChoice["rows"] }> = ({ rows }) => (
  <div
    aria-hidden="true"
    className="pointer-events-none mt-4 select-none rounded-md border border-line bg-white"
  >
    <div className="flex items-center justify-between px-3 py-2.5 text-[13px] text-ink-subtle">
      Search dish
      <ChevronUp className="h-4 w-4 text-ink-muted" />
    </div>
    <div className="mx-px mb-px rounded-b-md border-t border-line shadow-[0px_4px_12px_0px_#0000000F]">
      <div className="flex items-center gap-2 border-b border-[#E0E0E0] bg-[#F8FEFF] px-3 py-2 text-[12px] text-ink-muted">
        <DishMark className="h-4 w-4 flex-shrink-0" />
        <span>= Your dishes</span>
      </div>
      {rows.map((row) => (
        <div
          key={row.name}
          className="flex items-center justify-between px-3 py-2 text-[13px] text-ink"
        >
          {row.name}
          {row.own && <DishMark className="h-4 w-4 flex-shrink-0 text-brand" />}
        </div>
      ))}
    </div>
  </div>
);

/**
 * My brand → Configuration: how the plan builder behaves for this workspace.
 *
 * Under My brand rather than Settings, beside the form and the plans the
 * practice sells: it is a choice about how this practice writes its plans,
 * not about the account.
 *
 * One setting so far — which dishes the Dish field searches — and the same
 * one the switch on the plan builder's header flips; both read and write
 * `useDishScope`, so a change here is on every open plan at once.
 */
const BrandConfigurationTab: React.FC = () => {
  const toast = useToast();
  const { scope, setScope, canNarrow } = useDishScope();

  const choose = (next: DishScope) => {
    if (next === scope) return;
    // The card is disabled when this is false, so an ordinary click never
    // gets here; a keyboard on a card still settling can.
    if (next === "mine" && canNarrow !== true) return;
    setScope(next).catch((reason) => {
      toast.error(
        "Could not change the dish search",
        reason instanceof ApiError
          ? reason.message
          : "We could not reach the server. Check your connection and try again."
      );
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <section className="rounded-md border border-line bg-white px-5 py-4 3xl:px-6 3xl:py-5">
        <h2 className="text-[15px] 3xl:text-[16px] font-medium text-ink">
          Dish search in meal plans
        </h2>
        <p className="mt-1 text-[13px] 3xl:text-[14px] text-ink-muted">
          Choose which dishes appear when you search for a dish while creating a
          meal plan.
        </p>

        {/* Two cards and a radio in each: the whole card is the target, since
            the picture under the words is most of what is being chosen. */}
        <div
          role="radiogroup"
          aria-label="Dish search in meal plans"
          className="mt-5 grid gap-4 lg:grid-cols-2"
        >
          {CHOICES.map((choice) => {
            const selected = choice.scope === scope;
            // "My dishes only" waits on a dish to be only: without one the
            // card is drawn but cannot be picked, and says why beneath. Still
            // shown as selected if the server holds it, so what is set is
            // never hidden — the way out is the other card.
            const unavailable = choice.scope === "mine" && canNarrow !== true;
            return (
              <div key={choice.scope} className="flex flex-col gap-2">
                <button
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  disabled={unavailable}
                  onClick={() => choose(choice.scope)}
                  className={`rounded-md border p-4 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 disabled:cursor-not-allowed disabled:opacity-60 ${
                    selected
                      ? "border-[#5BD3E1] bg-[#F1FCFD]"
                      : "border-line bg-white enabled:hover:border-[#5BD3E1]"
                  }`}
                >
                  <div className="flex items-start gap-2.5">
                    {/* The radio's dot, drawn rather than a native input so the
                        whole card can be the button that ticks it. The dot is
                        inset from every edge of the ring rather than centred by
                        flex, so it cannot drift inside a button's own text
                        layout: the same distance on all four sides is centred
                        by definition. */}
                    <span
                      aria-hidden="true"
                      className={`relative mt-[3px] h-4 w-4 flex-shrink-0 rounded-full border ${
                        selected ? "border-brand" : "border-[#B8BFC7]"
                      }`}
                    >
                      {selected && (
                        <span className="absolute inset-[3px] rounded-full bg-brand" />
                      )}
                    </span>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[15px] 3xl:text-[16px] font-medium text-ink">
                          {choice.title}
                        </span>
                        {choice.scope === "all" && (
                          <span className="rounded-full bg-[#D7F9FD] px-2 py-0.5 text-[11px] font-medium text-[#007E8C]">
                            Default
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-[13px] 3xl:text-[14px] text-ink-muted">
                        {choice.description}
                      </p>
                    </div>
                  </div>

                  <ListPreview rows={choice.rows} />
                </button>
                {/* Outside the button, since a link inside a disabled one
                    cannot be followed. Only once the count is known to be
                    none — while it is still being read the card is merely
                    held, and there is nothing to say yet. */}
                {choice.scope === "mine" && canNarrow === false && (
                  <p className="flex items-center gap-1.5 text-[12px] 3xl:text-[13px] text-ink-muted">
                    <Info aria-hidden="true" className="h-3.5 w-3.5 flex-shrink-0" />
                    You have no dishes of your own yet. Add at least one in{" "}
                    <Link
                      to={ROUTES.dishes}
                      className="rounded font-medium text-brand hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
                    >
                      Dishes
                    </Link>{" "}
                    to choose this.
                  </p>
                )}
              </div>
            );
          })}
        </div>

        <p className="mt-4 flex items-center gap-1.5 text-[12px] 3xl:text-[13px] text-ink-subtle">
          <Info aria-hidden="true" className="h-3.5 w-3.5 flex-shrink-0" />
          Applies to all meal plans. You can also switch it from the meal plan
          screen anytime.
        </p>
      </section>
    </div>
  );
};

export default BrandConfigurationTab;
